import { useEffect, useRef, useState } from "react";
import BeerInfo from "../../components/BeerInfo/BeerInfo";
import BeerSelect from "../../components/BeerSelect/BeerSelect";
import BeerSlot from "../../components/BeerSlot/BeerSlot";
import FlightHistory from "../../components/FlightHistory/FlightHistory";
import { Beer, BeerFlight, SavedBeerFlight } from "../../data/types";
import { saveFlightToHistory } from "../../services/flightHistory";
import { getBeers, getRecentBeerFlight } from "../../services/firestore";
// import Loading from "../../components/Loading/Loading";

const Home: React.FC = () => {
  const [selectedCircle, setSelectedCircle] = useState<number | null>(null);
  const [beers, setBeers] = useState<Beer[]>([]);
  const [isSelectPopupOpen, setIsSelectPopupOpen] = useState(false);
  const [beerInfo, setBeerInfo] = useState<Beer | null>(null);
  const [beerSelections, setBeerSelections] = useState<(Beer | null)[]>(
    Array(5).fill(null)
  );
  const [beerFlight, setBeerFlight] = useState<BeerFlight | null>(null);
  const [isCustomFlight, setIsCustomFlight] = useState(false);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  // const [loading, setLoading] = useState<boolean>(true);
  const pressTimer = useRef<NodeJS.Timeout | null>(null);
  const isScrolling = useRef(false);
  const hasUserEdited = useRef(false);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const hasScrolled = useRef(false);

  useEffect(() => {
    setTimeout(() => {
      if (!hasScrolled.current) {
        setShowScrollIndicator(true);
      }
    }, 3000);

    const handleScroll = () => {
      if (!hasScrolled.current) {
        hasScrolled.current = true;
        setShowScrollIndicator(false);
      }
    };

    window.addEventListener("scroll", handleScroll, { capture: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    const asyncFunc = async () => {
      const beers = await getBeers();
      const fetchedBeerFlight = await getRecentBeerFlight();
      setBeers(beers);

      if (!fetchedBeerFlight) return;

      setBeerSelections(fetchedBeerFlight.beers);

      setBeerFlight(fetchedBeerFlight as BeerFlight);
      // setLoading(false);
    };
    asyncFunc();
  }, []);

  useEffect(() => {
    if (!hasUserEdited.current) return;
    if (!beerSelections.every((beer) => beer !== null)) return;

    const saved = saveFlightToHistory(beerSelections);
    if (saved) {
      setHistoryRefreshKey((key) => key + 1);
    }
  }, [beerSelections]);

  useEffect(() => {
    if (beerInfo || isSelectPopupOpen) {
      document.body.style.overflowY = "hidden";
      document.documentElement.style.overflowY = "hidden";

      if (!window.history.state || !window.history.state.popup) {
        window.history.pushState({ popup: true }, "");
      }

      const handlePopState = () => {
        if (beerInfo) {
          setBeerInfo(null);
        }
        if (isSelectPopupOpen) {
          setIsSelectPopupOpen(false);
        }
      };

      window.addEventListener("popstate", handlePopState);

      return () => {
        window.removeEventListener("popstate", handlePopState);
      };
    } else {
      document.body.style.overflowY = "auto";
      document.documentElement.style.overflowY = "auto";
    }
  }, [beerInfo, isSelectPopupOpen]);

  const handleCircleClick = (index: number) => {
    setSelectedCircle(index);
    setIsSelectPopupOpen(true);
  };

  const handleLongPressStart = () => {
    pressTimer.current = setTimeout(() => {
      hasUserEdited.current = true;
      setIsCustomFlight(true);
      setBeerSelections([null, null, null, null, null]);
    }, 500);
  };

  const handleLongPressEnd = () => {
    if (pressTimer.current) {
      clearTimeout(pressTimer.current);
    }
  };

  const handleTouchMove = () => {
    isScrolling.current = true;
    if (pressTimer.current) {
      clearTimeout(pressTimer.current);
    }
  };

  const handleBeerSelect = (beer: Beer) => {
    if (selectedCircle !== null) {
      hasUserEdited.current = true;
      setIsCustomFlight(true);
      const updatedSelections = [...beerSelections];
      updatedSelections[selectedCircle] = beer;
      setBeerSelections(updatedSelections);
      setIsSelectPopupOpen(false);
      setSelectedCircle(null);
    }
  };

  const handleHistorySelect = (flight: SavedBeerFlight) => {
    hasUserEdited.current = false;
    setBeerSelections(flight.beers);
    setIsCustomFlight(true);
  };

  return (
    <>
      <FlightHistory
        onSelectFlight={handleHistorySelect}
        refreshKey={historyRefreshKey}
      />

      <div className="body">
        {/* loading && <Loading /> */}
        <div className="hero">
          <h2>
            {isCustomFlight
              ? "Din beer flight"
              : beerFlight?.name
                ? `${beerFlight.name}`
                : "Tap the circles to build your beer flight"}
          </h2>
          <p>Reset by holding your finger on the flight</p>

          {!isCustomFlight && beerFlight?.description && (
            <p>{beerFlight?.description}</p>
          )}

          <div
            className="beerflight-div"
            onTouchStart={handleLongPressStart}
            onTouchEnd={handleLongPressEnd}
            onMouseDown={handleLongPressStart}
            onMouseUp={handleLongPressEnd}
            onTouchMove={handleTouchMove}
          >
            <img
              className="beerflight"
              src="/flight.svg"
              alt="The beerflight"
            />
            <div className="circles-container">
              {beerSelections.map((beer, index) => (
                <div
                  key={index}
                  className="circle"
                  onClick={() => handleCircleClick(index)}
                >
                  {beer ? <img src={beer.image} alt={beer.name} /> : index + 1}
                </div>
              ))}
              <img src="/Logo.png" alt="BarlindBeer" className="flight-logo" />
            </div>
          </div>
        </div>

        <div className="beers-grid">
          {beerSelections.map((beer, index) => (
            <BeerSlot
              key={index}
              beer={beer}
              index={index + 1}
              handleSelect={() => {
                if (beer) setBeerInfo(beer);
              }}
              onClick={handleCircleClick}
            />
          ))}
        </div>
      </div>

      <div
        className={`${
          showScrollIndicator
            ? "scroll-down-indicator scroll-down-indicator--show"
            : "scroll-down-indicator scroll-down-indicator--hide"
        }`}
      >
        <div>
          <p>Scroll down</p>
          <span>↓</span>
        </div>
      </div>

      {isSelectPopupOpen && (
        <BeerSelect
          onSelect={handleBeerSelect}
          beers={beers}
          onClose={() => setIsSelectPopupOpen(false)}
        />
      )}

      {beerInfo && (
        <BeerInfo beer={beerInfo} onClose={() => setBeerInfo(null)} />
      )}
    </>
  );
};

export default Home;
